import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import {
    LocalStoreService,
    PermissionsService,
    SessionRepository,
    ProjectService,
    SystemService,
    SystemStore
} from '@frontend-site/shared/core';
import { Observable, of } from 'rxjs';
import { ProjectInfo, SystemDetails } from '@frontend-site/shared/data';
import { AiConsentDialogComponent } from '@common/ui/dialogs/ai-consent-dialog/ai-consent-dialog.component';
import { PDialogService } from '@frontend-site/ui/components';
import { AiModeService } from '@panaya/ai-chat/features';
import { SeemoreConfigurationService } from '@modules/settings/configurations/development-configuration/seemore-configuration/seemore-configuration.service';
import { switchMap, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class AiConsentToProjectGuard  {
    private storageKey: string;
    private systemId: number;
    constructor(
        private dialog: PDialogService,
        private projSrv: ProjectService,
        private localStorage: LocalStoreService,
        private seemoreSrv: SeemoreConfigurationService,
        private sessionStore: SessionRepository,
        private permSrv: PermissionsService,
        private systemSrv: SystemService,
        private sysStore: SystemStore,
        private aiSrv: AiModeService
    ) {}
    private isAllowedToUpdate(systemId: number, projId: number): boolean {
        const hasPermissions = this.permSrv.hasPermissions('Seemore intelligence (Gen-AI) : manage', [projId]);
        const dontShowPopup = this.getFromLocalStorage(systemId);
        return hasPermissions && !dontShowPopup;
    }
    private getFromLocalStorage(sysId: number): boolean {
        this.systemId = sysId;
        this.storageKey = `AiConsent-${sysId}-dontShowAgain`;
        let noThankYouClicked = sessionStorage.getItem(this.storageKey) != null;
        let dontShowMeAgainClicked = this.localStorage.getData(this.storageKey) != null;
        return noThankYouClicked || dontShowMeAgainClicked;
    }
    private openAiConsentPopup(systemDetails: SystemDetails): void {
        if (systemDetails && this.aiSrv.isConsentPopupEnabled(systemDetails.aiEnablement, systemDetails.aiConsent))
            this.showDialog().subscribe((value: boolean) => this.updateAiConsent(value));
    }
    private showDialog(): Observable<boolean> {
        const dialogConfig = {
            panelClass: 'p-dialog-small',
            data: { storagekey: this.storageKey, systemId: this.systemId }
        };
        return this.dialog.open(AiConsentDialogComponent, dialogConfig).beforeClosed();
    }
    private updateAiConsent(value: boolean): void {
        if (!!value === value && value)
            this.seemoreSrv
                .setSeemoreConfiguration(this.systemId, value)
                .pipe(
                    switchMap(() => this.systemSrv.getSystemDetails(this.systemId)),
                    tap((sys: SystemDetails) => this.sysStore.updateSystemDetails(this.systemId, sys))
                )
                .subscribe();
    }
    private isExecuteiaUser(): boolean {
        return this.sessionStore?.applicationConfig?.user?.email?.endsWith('@execute.ia');
    }

    canActivate(route: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Observable<boolean> {
        const { projectId } = route.params;
        if (projectId && !this.isExecuteiaUser()) {
            const projId = Number(projectId);
            const sysDetails = this.sysStore?.systemDetails;
            sysDetails ? this.openPopup(sysDetails, projId) : this.loadDataAndOpenPopup(projId);
        }
        return of(true);
    }

    private openPopup(sysDetails: SystemDetails, projId: number): void {
        if (this.isAllowedToUpdate(sysDetails.systemId, projId)) {
            this.openAiConsentPopup(sysDetails);
        }
    }
    private loadDataAndOpenPopup(projId: number) {
        this.projSrv.loadProjectDetails(projId).subscribe((proj: ProjectInfo) => {
            if (this.isAllowedToUpdate(proj.systemId, projId)) {
                this.systemSrv
                    .loadSystemDetails(proj.systemId)
                    .subscribe((sysDetails: SystemDetails) => this.openAiConsentPopup(sysDetails));
            }
        });
    }
}
